import type { TemplatePositionStateModel as AppTemplatePositionStateModel } from '@sosocio/creator-types/app';
import type {
	TemplatePositionPhotoModel,
	TemplatePositionStateModel,
} from '@sosocio/creator-types/database';

type TemplatePositionIdentifier = AppTemplatePositionStateModel['id'];

interface ResolvedTemplatePosition<
	State extends TemplatePositionStateLike,
	PhotoPosition extends TemplatePositionGeometryLike,
	TextPosition extends TemplatePositionGeometryLike,
> {
	available: true;
	position: PhotoPosition | TextPosition;
	state: State;
	type: 'photo' | 'text';
}

interface ResolvedTemplatePositionSet<
	State extends TemplatePositionStateLike,
	PhotoPosition extends TemplatePositionGeometryLike,
	TextPosition extends TemplatePositionGeometryLike,
> {
	id: string;
	positions: Array<ResolvedTemplatePosition<State, PhotoPosition, TextPosition>>;
	priority: number;
}

interface ResolveTemplatePositionSetsOptions<
	State extends TemplatePositionStateLike,
	PhotoPosition extends TemplatePositionGeometryLike,
	TextPosition extends TemplatePositionGeometryLike,
> {
	photoPositions: ReadonlyArray<PhotoPosition>;
	states: ReadonlyArray<State>;
	textPositions: ReadonlyArray<TextPosition>;
}

type TemplatePositionGeometryLike = (
	Pick<
		TemplatePositionPhotoModel,
		| 'angle'
		| 'borderwidth'
		| 'height'
		| 'width'
		| 'x_axis'
		| 'y_axis'
	>
	& Partial<Pick<
		TemplatePositionPhotoModel,
		| 'overlap_bottom'
		| 'overlap_left'
		| 'overlap_right'
		| 'overlap_top'
	>>
	& {
		templatestateid: TemplatePositionIdentifier | null;
		transformable: boolean | number;
	}
);

type TemplatePositionStateLike = (
	Pick<
		TemplatePositionStateModel,
		'priority'
	>
	& {
		combine: boolean | number;
		id: TemplatePositionIdentifier;
		type: string;
	}
);

interface PositionBounds {
	bottom: number;
	left: number;
	right: number;
	top: number;
}

type CombinationOptions = Record<string, TemplatePositionIdentifier[]>;

interface CombinationTree {
	[identifier: string]: CombinationTree | false;
}

/**
 * Determines whether two authored positions may appear in the same fixed-template variant.
 * Priority groups require both states to opt into combination, while authored overlap margins
 * define the geometry that participates in collision detection.
 *
 * @param left First resolved position under comparison.
 * @param right Second resolved position under comparison.
 * @returns Whether the positions may be rendered together.
 */
function areTemplatePositionsCompatible<
	State extends TemplatePositionStateLike,
	PhotoPosition extends TemplatePositionGeometryLike,
	TextPosition extends TemplatePositionGeometryLike,
>(
	left: ResolvedTemplatePosition<State, PhotoPosition, TextPosition>,
	right: ResolvedTemplatePosition<State, PhotoPosition, TextPosition>,
): boolean {
	if (
		left.state.priority !== right.state.priority
		&& (!left.state.combine || !right.state.combine)
	) {
		return false;
	}

	return !doTemplatePositionsOverlap(
		left.position,
		right.position,
	);
}

/**
 * Flattens the compatibility tree into the ordered identifier paths used as template variants.
 *
 * @param tree Recursive compatibility tree produced from the variant roots.
 * @returns Identifier paths in the same traversal order as the authored states.
 */
function collectCombinationPaths(tree: CombinationTree): string[][] {
	const paths: string[][] = [];

	Object.entries(tree).forEach(([
		identifier,
		childTree,
	]) => {
		if (childTree === false) {
			paths.push([identifier]);
			return;
		}

		collectCombinationPaths(childTree).forEach((childPath) => {
			paths.push([
				identifier,
				...childPath,
			]);
		});
	});

	return paths;
}

/**
 * Calculates the axis-aligned bounds used by Creator's fixed-template overlap rules.
 * Quarter-turn rotations swap the effective width and height around the authored center.
 *
 * @param position Authored template position geometry.
 * @returns Border-inclusive axis-aligned bounds for overlap detection.
 */
function createTemplatePositionBounds(
	position: TemplatePositionGeometryLike,
): PositionBounds {
	const overlapLeft = position.overlap_left ?? 0;
	const overlapRight = position.overlap_right ?? 0;
	const overlapTop = position.overlap_top ?? 0;
	const overlapBottom = position.overlap_bottom ?? 0;
	const x = position.x_axis + overlapLeft + 1;
	const y = position.y_axis + overlapTop + 1;
	const width = position.width - overlapLeft - overlapRight - 2;
	const height = position.height - overlapTop - overlapBottom - 2;
	const unrotatedBounds: PositionBounds = {
		bottom: y + height + position.borderwidth,
		left: x - position.borderwidth,
		right: x + width + position.borderwidth,
		top: y - position.borderwidth,
	};

	if (position.angle !== 90 && position.angle !== 270) {
		return unrotatedBounds;
	}

	const centerX = (unrotatedBounds.left + unrotatedBounds.right) / 2;
	const centerY = (unrotatedBounds.top + unrotatedBounds.bottom) / 2;
	const rotatedWidth = unrotatedBounds.bottom - unrotatedBounds.top;
	const rotatedHeight = unrotatedBounds.right - unrotatedBounds.left;

	return {
		bottom: centerY + (rotatedHeight / 2),
		left: centerX - (rotatedWidth / 2),
		right: centerX + (rotatedWidth / 2),
		top: centerY - (rotatedHeight / 2),
	};
}

/**
 * Recursively expands each root's compatible partners into maximal compatible paths.
 * The traversal intentionally preserves the legacy fixed-template variant ordering.
 *
 * @param options Available partners for every current root.
 * @param compatibility Complete compatibility graph keyed by state identifier.
 * @returns Recursive tree whose leaf paths describe candidate template variants.
 */
function createTemplatePositionCombinationTree(
	options: CombinationOptions,
	compatibility: CombinationOptions,
): CombinationTree {
	const tree: CombinationTree = {};

	Object.keys(options).forEach((rootIdentifier) => {
		const optionIdentifiers = options[rootIdentifier];

		if (optionIdentifiers.length === 0) {
			tree[rootIdentifier] = false;
			return;
		}

		if (optionIdentifiers.length === 1) {
			tree[rootIdentifier] = {
				[String(optionIdentifiers[0])]: false,
			};
			return;
		}

		const childOptions: CombinationOptions = {};

		optionIdentifiers.forEach((optionIdentifier, optionIndex) => {
			const compatibleOptions = optionIdentifiers.filter((candidateIdentifier, candidateIndex) => (
				optionIndex !== candidateIndex
				&& compatibility[String(optionIdentifier)]?.includes(candidateIdentifier)
			));
			const duplicateIdentifier = compatibleOptions.find((candidateIdentifier) => (
				childOptions[String(candidateIdentifier)]?.includes(optionIdentifier)
			));

			if (!duplicateIdentifier) {
				childOptions[String(optionIdentifier)] = compatibleOptions;
			}
		});

		tree[rootIdentifier] = createTemplatePositionCombinationTree(
			childOptions,
			compatibility,
		);
	});

	return tree;
}

/**
 * Checks whether two border-inclusive authored position rectangles intersect.
 *
 * @param left First template position geometry.
 * @param right Second template position geometry.
 * @returns Whether the effective rectangles overlap.
 */
function doTemplatePositionsOverlap(
	left: TemplatePositionGeometryLike,
	right: TemplatePositionGeometryLike,
): boolean {
	const leftBounds = createTemplatePositionBounds(left);
	const rightBounds = createTemplatePositionBounds(right);

	return (
		leftBounds.left < rightBounds.right
		&& leftBounds.right > rightBounds.left
		&& leftBounds.top < rightBounds.bottom
		&& leftBounds.bottom > rightBounds.top
	);
}

/**
 * Orders numeric template state identifiers using their persisted numeric value.
 *
 * @param left First serialized identifier.
 * @param right Second serialized identifier.
 * @returns Sort comparison value compatible with the legacy Creator ordering.
 */
function sortTemplatePositionIdentifiers(
	left: string,
	right: string,
): number {
	return Number.parseInt(
		left,
		10,
	) - Number.parseInt(
		right,
		10,
	);
}

/**
 * Resolves all compatible fixed-template position sets from independently loaded
 * state, photo-position, and text-position collections. The result preserves the
 * Creator variant ordering, fixed-text inclusion, priority, combine, and overlap rules.
 *
 * @param options Collections needed to join and resolve fixed-template positions.
 * @returns Ordered fixed-template variants containing every selected photo and text position.
 */
export function resolveTemplatePositionSets<
	State extends TemplatePositionStateLike,
	PhotoPosition extends TemplatePositionGeometryLike,
	TextPosition extends TemplatePositionGeometryLike,
>(
	options: ResolveTemplatePositionSetsOptions<State, PhotoPosition, TextPosition>,
): Array<ResolvedTemplatePositionSet<State, PhotoPosition, TextPosition>> {
	const photoPositionByStateId = new Map<string, PhotoPosition>();
	const textPositionByStateId = new Map<string, TextPosition>();

	options.photoPositions.forEach((position) => {
		if (
			position.templatestateid !== null
			&& !photoPositionByStateId.has(String(position.templatestateid))
		) {
			photoPositionByStateId.set(
				String(position.templatestateid),
				position,
			);
		}
	});
	options.textPositions.forEach((position) => {
		if (
			position.templatestateid !== null
			&& !textPositionByStateId.has(String(position.templatestateid))
		) {
			textPositionByStateId.set(
				String(position.templatestateid),
				position,
			);
		}
	});

	const photoPositions: Array<ResolvedTemplatePosition<State, PhotoPosition, TextPosition>> = [];
	const textPositions: Array<ResolvedTemplatePosition<State, PhotoPosition, TextPosition>> = [];

	options.states.forEach((state) => {
		const stateIdentifier = String(state.id);

		if (state.type === 'photo') {
			const position = photoPositionByStateId.get(stateIdentifier);

			if (position) {
				photoPositions.push({
					available: true,
					position,
					state,
					type: 'photo',
				});
			}
			return;
		}

		if (state.type === 'text') {
			const position = textPositionByStateId.get(stateIdentifier);

			if (position) {
				textPositions.push({
					available: true,
					position,
					state,
					type: 'text',
				});
			}
		}
	});

	const transformableTextPositions = textPositions.filter(({ position }) => Boolean(position.transformable));
	const fixedTextPositions = textPositions.filter(({ position }) => !position.transformable);
	const fixedTextPositionsWithPhotoGroup = fixedTextPositions.filter(({ state }) => (
		photoPositions.some((photoPosition) => photoPosition.state.priority === state.priority)
	));
	const fixedTextPositionsWithoutPhotoGroup = fixedTextPositions.filter(
		(position) => !fixedTextPositionsWithPhotoGroup.includes(position),
	);
	const compatibility: CombinationOptions = {};

	photoPositions.forEach((rootPosition) => {
		compatibility[String(rootPosition.state.id)] = photoPositions
			.filter((position) => (
				position !== rootPosition
				&& areTemplatePositionsCompatible(
					rootPosition,
					position,
				)
			))
			.map(({ state }) => state.id);
	});
	transformableTextPositions.forEach((rootPosition) => {
		const hasPhotoInPriorityGroup = photoPositions.some(
			(photoPosition) => photoPosition.state.priority === rootPosition.state.priority,
		);

		if (hasPhotoInPriorityGroup) {
			return;
		}

		compatibility[String(rootPosition.state.id)] = transformableTextPositions
			.filter((position) => (
				position !== rootPosition
				&& areTemplatePositionsCompatible(
					rootPosition,
					position,
				)
			))
			.map(({ state }) => state.id);
	});

	const combinationOptions: CombinationOptions = Object.fromEntries(
		Object.entries(compatibility).map(([
			identifier,
			optionIdentifiers,
		]) => [
			identifier,
			[...optionIdentifiers],
		]),
	);
	let combinationPaths: string[][] = [];

	if (Object.keys(compatibility).length) {
		combinationPaths = collectCombinationPaths(
			createTemplatePositionCombinationTree(
				combinationOptions,
				compatibility,
			),
		);
	} else if (fixedTextPositions.length) {
		combinationPaths = [[]];
	}

	const uniqueCombinations: Record<string, string[]> = {};

	combinationPaths.forEach((combinationPath) => {
		combinationPath.sort(sortTemplatePositionIdentifiers);
		uniqueCombinations[combinationPath.join('_')] = combinationPath;
	});

	const rootVariantPositions = [
		...transformableTextPositions,
		...photoPositions,
	];
	const resolvedSets = Object.keys(uniqueCombinations).map((combinationIdentifier) => {
		const variantPositions = uniqueCombinations[combinationIdentifier]
			.map((positionIdentifier) => rootVariantPositions.find(
				({ state }) => String(state.id) === positionIdentifier,
			))
			.filter((position): position is ResolvedTemplatePosition<State, PhotoPosition, TextPosition> => Boolean(position));
		const combinedPositions = [...variantPositions];

		transformableTextPositions.forEach((textPosition) => {
			const hasConflict = combinedPositions.some((variantPosition) => (
				!areTemplatePositionsCompatible(
					textPosition,
					variantPosition,
				)
			));

			if (!hasConflict) {
				combinedPositions.push(textPosition);
			}
		});

		const priority = combinedPositions.reduce(
			(highestPriority, { state }) => Math.max(
				highestPriority,
				state.priority,
			),
			0,
		);
		const accompanyingTransformableTextPositions = combinedPositions.filter(
			(position) => !variantPositions.includes(position),
		);
		const isPhotoVariant = variantPositions.some(({ type }) => type === 'photo');
		const primaryPositions = [
			...variantPositions,
			...(
				isPhotoVariant
					? []
					: fixedTextPositionsWithoutPhotoGroup
			),
		].sort((left, right) => sortTemplatePositionIdentifiers(
			String(left.state.id),
			String(right.state.id),
		));
		const accompanyingTextPositions = textPositions.filter((position) => (
			accompanyingTransformableTextPositions.includes(position)
			|| (
				isPhotoVariant
					? fixedTextPositions.includes(position)
					: fixedTextPositionsWithPhotoGroup.includes(position)
			)
		));
		const positions = [
			...primaryPositions,
			...accompanyingTextPositions,
		];

		return {
			id: positions.map(({ state }) => state.id).join('_'),
			positions,
			priority,
		};
	});

	return resolvedSets.sort((left, right) => left.priority - right.priority);
}
